import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Clock, ArrowRight } from "lucide-react";
import OfferArtwork from "./OfferArtwork";
import OfferBadge from "./OfferBadge";
import { useOfferCountdown } from "../../hooks/use-offer-countdown";
import { resolveOfferTheme, ensureOfferFont } from "../../lib/offers/offerTheme";
import "./offers.css";

const DISMISS_KEY_PREFIX = "pb_offer_banner_dismissed_";

const readDismissed = (slug) => {
  if (!slug) return false;
  try {
    return localStorage.getItem(`${DISMISS_KEY_PREFIX}${slug}`) === "true";
  } catch {
    return false;
  }
};

const writeDismissed = (slug) => {
  try {
    localStorage.setItem(`${DISMISS_KEY_PREFIX}${slug}`, "true");
  } catch {
    // Storage blocked (private mode): the banner simply comes back next visit.
  }
};

/**
 * Festive campaign banner, rendered entirely from a PublicOffer.
 *
 * Hides itself when the countdown reaches zero and calls `onExpire` once so
 * the caller can re-fetch. `dismissible` banners remember the × per offer slug.
 */
const OfferBanner = ({ offer, clockOffsetMs = 0, dismissible = false, onCta, onExpire, className = "" }) => {
  const display = useMemo(() => offer?.display || {}, [offer?.display]);
  const theme = useMemo(() => resolveOfferTheme(display), [display]);
  const { isExpired, isLastDay, label, announcement } = useOfferCountdown(offer?.endAt, clockOffsetMs);
  const [dismissed, setDismissed] = useState(() => readDismissed(offer?.slug));
  const expiryReported = useRef(false);

  useEffect(() => {
    ensureOfferFont(theme);
  }, [theme]);

  useEffect(() => {
    setDismissed(readDismissed(offer?.slug));
    expiryReported.current = false;
  }, [offer?.slug, offer?.endAt]);

  useEffect(() => {
    if (!isExpired || expiryReported.current) return;
    expiryReported.current = true;
    if (typeof onExpire === "function") onExpire();
  }, [isExpired, onExpire]);

  if (!offer || isExpired || (dismissible && dismissed)) return null;

  const title = display.title || offer.name;
  const ctaText = display.ctaText || "View plans";
  const showCountdown = display.showCountdown !== false && Boolean(label);

  const handleDismiss = () => {
    writeDismissed(offer.slug);
    setDismissed(true);
  };

  return (
    <section
      aria-label={title}
      className={`pb-offer-banner relative overflow-hidden rounded-2xl shadow-lg ${className}`}
      style={{
        background: theme.bannerImageUrl
          ? `linear-gradient(90deg, ${theme.secondary} 0%, ${theme.secondary}E6 45%, ${theme.primary}99 100%), url("${theme.bannerImageUrl}") center / cover no-repeat`
          : theme.background,
        color: theme.text,
      }}
    >
      {!theme.bannerImageUrl && (
        <OfferArtwork
          theme={theme}
          className="pb-offer-banner__art"
        />
      )}

      {/* Layout lives in offers.css: it follows the banner's own width, so it
          stacks correctly next to the sidebar as well as on phones. */}
      <div className="pb-offer-banner__body">
        <div className="min-w-0 flex-1">
          {display.badgeText && <OfferBadge theme={theme} text={display.badgeText} className="mb-2" />}
          <div className="pb-offer-banner__heading">
            <h2
              className="pb-offer-banner__title break-words font-bold"
              style={{ fontFamily: theme.headingFont || undefined }}
            >
              {title}
            </h2>
            {display.subtitle && (
              <>
                <span
                  aria-hidden="true"
                  className="pb-offer-banner__divider"
                  style={{ backgroundColor: theme.accentSoft, opacity: 0.6 }}
                />
                <p className="pb-offer-banner__subtitle break-words font-medium" style={{ color: theme.mutedText }}>
                  {display.subtitle}
                </p>
              </>
            )}
          </div>

          {showCountdown && (
            <>
              <p
                aria-hidden="true"
                className={`mt-3 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold tabular-nums sm:text-sm ${isLastDay ? "pb-offer-urgent" : ""}`}
                style={
                  isLastDay
                    ? { background: theme.badgeBackground, color: theme.onAccent }
                    : { border: `1px solid ${theme.accentSoft}`, color: theme.text }
                }
              >
                <Clock size={14} aria-hidden="true" />
                {label}
              </p>
              <span className="sr-only" aria-live="polite" aria-atomic="true">
                {announcement}
              </span>
            </>
          )}
        </div>

        {typeof onCta === "function" && (
          <button
            type="button"
            onClick={onCta}
            className="pb-offer-banner__cta inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-black shadow-md transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ background: theme.badgeBackground, color: theme.onAccent }}
          >
            {ctaText}
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss offer banner"
          className="absolute right-2 top-2 rounded-full p-2 transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2"
          style={{ color: theme.text }}
        >
        </button>
      )}
    </section>
  );
};

export default OfferBanner;
